import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isBefore,
  isAfter,
} from 'date-fns'

/** Build Sun–Sat week rows covering an entire calendar month (including leading/trailing days). */
export function buildCalendarWeeks(currentMonth: Date): Date[][] {
  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 })
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })
  const days = eachDayOfInterval({ start: calStart, end: calEnd })
  const weeks: Date[][] = []
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7))
  }
  return weeks
}

export type WeekSpanSegment<T> = {
  event: T
  startCol: number
  endCol: number
  weekStart: Date
}

/**
 * Clip events with [from, to] date ranges to a Sun–Sat week and produce grid column spans.
 */
export function getWeekSpanSegments<T extends { from: Date; to: Date }>(
  events: T[],
  weekStart: Date,
  sortCompare: (a: WeekSpanSegment<T>, b: WeekSpanSegment<T>) => number
): WeekSpanSegment<T>[] {
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 })
  const segments: WeekSpanSegment<T>[] = []
  for (const event of events) {
    if (isAfter(event.from, weekEnd) || isBefore(event.to, weekStart)) continue
    const barStart = isBefore(event.from, weekStart) ? weekStart : event.from
    const barEnd = isAfter(event.to, weekEnd) ? weekEnd : event.to
    const startCol = (barStart.getDay() + 7) % 7
    const endCol = (barEnd.getDay() + 7) % 7
    segments.push({ event, startCol, endCol, weekStart })
  }
  return segments.sort(sortCompare)
}

/** AR Team calendar: full-week bars first, then last-name order (matches previous behavior). */
export function compareArLeadershipSegments<
  T extends { from: Date; to: Date; fullname: string },
>(a: WeekSpanSegment<T>, b: WeekSpanSegment<T>): number {
  const isFullWeek = (seg: WeekSpanSegment<T>) => seg.startCol === 0 && seg.endCol === 6
  const startsOnSunday = (seg: WeekSpanSegment<T>) => seg.startCol === 0
  const getLastName = (fullname: string) => {
    const parts = fullname.trim().split(/\s+/)
    return (parts[parts.length - 1] ?? fullname).toLowerCase()
  }
  const fullA = isFullWeek(a)
  const fullB = isFullWeek(b)
  if (fullA !== fullB) return fullA ? -1 : 1
  if (!fullA && !fullB) {
    const sunA = startsOnSunday(a)
    const sunB = startsOnSunday(b)
    if (sunA !== sunB) return sunA ? -1 : 1
    if (!sunA && !sunB && a.startCol !== b.startCol) return a.startCol - b.startCol
  }
  const lastA = getLastName(a.event.fullname)
  const lastB = getLastName(b.event.fullname)
  return lastA.localeCompare(lastB) || a.event.fullname.localeCompare(b.event.fullname)
}

/** Groups month view: full-week trips first, then project id / name. */
export function compareGroupTripSegments<
  T extends { from: Date; to: Date; sortKey: string },
>(a: WeekSpanSegment<T>, b: WeekSpanSegment<T>): number {
  const isFullWeek = (seg: WeekSpanSegment<T>) => seg.startCol === 0 && seg.endCol === 6
  const fullA = isFullWeek(a)
  const fullB = isFullWeek(b)
  if (fullA !== fullB) return fullA ? -1 : 1
  return a.event.sortKey.localeCompare(b.event.sortKey, undefined, { sensitivity: 'base' })
}
